/**
 * ErrorBoundary — captura erros de render e evita tela branca.
 * Reporta no registro de uso (`error.client`, com a mensagem e o stack limpos pelo servidor).
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import i18next from 'i18next';
import { reportClientError } from '../../lib/activity/observers';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  message?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message };
  }

  override componentDidCatch(error: Error, _info: ErrorInfo): void {
    reportClientError('render', error);
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      // Mesma tela de erro das rotas (RouteError): texto traduzido e a ação de recarregar.
      return (
        <div className="route-error error-boundary" role="alert">
          <h2>{i18next.t('route_error_title', 'Não conseguimos abrir esta tela')}</h2>
          <p>
            {i18next.t(
              'route_error_generic',
              'Recarregue a página. Se continuar, fale com o Suporte.',
            )}
          </p>
          <button type="button" className="button color" onClick={() => window.location.reload()}>
            {i18next.t('route_error_reload', 'Recarregar')}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
